import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { emailScannerImageUrl } from '../../../../../models/UiUtils';
import { useGetMetaAdsProvider } from '../../../../../queries/Ads/useGetMetaAdsProvider';
import {
	AccountOperationsViewModel,
	AdminPermission,
	AdminUserSessionContext,
} from '../../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../../web/components-v2/Button';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { CustomLeadParserModal } from '../../../../../web/components/CustomLeadParserModal';
import { BlueCardWithCta } from '../../../../../web/components/cards/BlueCardWithCta';
import { IntegrationCard } from '../../../../../web/components/integrations/IntegrationCard';
import { LeadParserCard } from '../../../../../web/components/LeadParserCard';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { InfoIcon } from '../../../../../web/components/svgs/icons/InfoIcon';
import { MetaMainLogo } from '../../../../../web/components/svgs/icons/MetaMainLogo';
import { TestLeadParserModal } from '../../../../../web/components/TestLeadParserModal';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { ConditionalAccess } from '../../../../components/ConditionalAccess';
import { MetaAdsAdminModal } from '../../../../components/integrations/MetaAdsAdminModal';
import { styleSheet } from './styles';

interface IProps {
	accountVM?: AccountOperationsViewModel;
	adminUserSession?: AdminUserSessionContext;
	className?: string;
	styles?: StyleDeclarationValue[];
}

const _AdminLeadParsers = ({ accountVM, adminUserSession, className, styles }: IProps) => {
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();
	const { logApiError } = useEventLogging();
	const [manageParserModalIsVisible, setManageParserModalIsVisible] = useState(false);
	const [testParserModalIsVisible, setTestParserModalIsVisible] = useState(false);
	const [parserToManage, setParserToManage] = useState<Api.ICustomLeadParser | undefined>(undefined);
	const [parserToTest, setParserToTest] = useState<Api.ICustomLeadParser | undefined>(undefined);
	const [processing, setProcessing] = useState(false);
	const [isMetaAdsModalOpen, setIsMetaAdsModalOpen] = useState(false);

	const metaImpersonationContext = accountVM?.account?.id ? { account: { id: accountVM.account.id } } : undefined;
	const { data: metaAdsProvider } = useGetMetaAdsProvider({
		impersonationContext: metaImpersonationContext,
		enabled: !!metaImpersonationContext,
	});
	const isMetaAdsEnabled = metaAdsProvider?.enabled ?? false;

	const [account] = useState<AccountOperationsViewModel>(
		new AccountOperationsViewModel(userSession as AdminUserSessionContext, accountVM.account)
	);
	const [emailScannersVm, setEmailScannersVm] = useState<Api.EmailScannersViewModel>(() => {
		const vm = new Api.EmailScannersViewModel(userSession as AdminUserSessionContext, accountVM.account);
		vm.impersonate({ account: accountVM.account });
		return vm;
	});

	useEffect(() => {
		emailScannersVm.loadStats();
		account
			.load()
			.then(() => {
				// Re-create VM with loaded account data so scanners are properly derived
				const loadedAccount = account.account;
				if (loadedAccount?.features?.emailScan) {
					const vm = new Api.EmailScannersViewModel(userSession as AdminUserSessionContext, loadedAccount);
					vm.impersonate({ account: loadedAccount });
					vm.loadStats();
					setEmailScannersVm(vm);
				}
			})
			.catch((error: Api.IOperationResultNoValue) => {
				logApiError('GetAccount-Error', error);
				errorMessages.push({
					messages: ['Could not load the account'],
				});
			});
		// `emailScannersVm` is intentionally excluded: this effect calls `setEmailScannersVm`,
		// and including it would re-fire on every VM swap.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [account, errorMessages, logApiError, userSession]);

	const enableScanner = async ({
		scannerId,
		shouldEnableScanner,
		type,
	}: {
		scannerId: string;
		shouldEnableScanner: boolean;
		type: Api.EmailScannerType;
	}) => {
		const enableText = shouldEnableScanner ? 'enable' : 'disable';
		setProcessing(true);

		try {
			const action =
				type === Api.EmailScannerType.EmailScanner
					? emailScannersVm.enableEmailScanner(scannerId)
					: emailScannersVm.enableCustomLeadParser(scannerId);
			await action;

			toast.success(
				`${
					type === Api.EmailScannerType.EmailScanner ? 'Email scanner' : 'Custom lead source'
				} ${enableText}d successfully`
			);
			setProcessing(false);
		} catch (error) {
			logApiError('EnableEmailScannerParser-Error', error);
			errorMessages.pushApiError(
				error,
				`Failed to ${enableText} ${type === Api.EmailScannerType.EmailScanner ? 'email scanner' : 'custom lead source'}`
			);
			setProcessing(false);
		}
	};

	const onAddNewCustomLeadParserClick = () => {
		setManageParserModalIsVisible(true);
		setParserToManage(undefined);
	};

	const onEnableClick = (id: string, enable: boolean, type: Api.EmailScannerType) => {
		enableScanner({
			shouldEnableScanner: enable,
			scannerId: id,
			type,
		});
	};

	const onLeadParserCheckboxChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const automationFeatures: Api.IAutomationFeatures = {
			...account?.account?.features?.automation,
			autoStartForLeads: e.target.checked,
		};
		await account?.updateAutomationFeatures(automationFeatures)?.catch((error: Api.IOperationResultNoValue) => {
			logApiError('UpdateAccountFeatures-Error', error);
			errorMessages.push({
				messages: ['Could not update the account features'],
			});
		});
		await account.load().catch((error: Api.IOperationResultNoValue) => {
			logApiError('GetAccount-Error', error);
			errorMessages.push({
				messages: ['Could not load the account'],
			});
		});
	};

	const onManageLeadParser = async (parser?: Api.ICustomLeadParser, cancel?: boolean) => {
		const shouldShow = !!parser && !cancel;
		setManageParserModalIsVisible(shouldShow);
		setParserToManage(shouldShow ? parser : undefined);
	};

	const onTestLeadParser = async (parser?: Api.ICustomLeadParser) => {
		setTestParserModalIsVisible(!!parser);
		setParserToTest(parser);
	};

	const onManageParserDelete = async (parser: Api.ICustomLeadParser) => {
		setProcessing(true);

		try {
			await emailScannersVm.deleteCustomLeadParser(parser.id);
			toast.success('Custom lead source deleted successfully');
			setManageParserModalIsVisible(false);
			setProcessing(false);
		} catch (error) {
			setProcessing(false);
			logApiError('DeleteCustomLeadParser-Error', error);
			errorMessages.pushApiError(error, 'Error deleting custom lead parser');
		}
	};

	const onManageParserSave = async (parser: Api.ICustomLeadParser, isNew: boolean) => {
		setProcessing(true);
		try {
			const parserAction = isNew
				? emailScannersVm.createCustomLeadParser(parser)
				: emailScannersVm.updateCustomLeadParser(parser);
			await parserAction;
			toast.success(`Custom lead source ${isNew ? 'created' : 'updated'} successfully`);
			setManageParserModalIsVisible(false);
			setProcessing(false);
		} catch (error) {
			setProcessing(false);
			logApiError('SaveCustomLeadParser-Error', error);
			errorMessages.pushApiError(error, 'Error creating custom lead source');
		}
	};

	const accountsAccount = account?.account;
	if (!accountsAccount) {
		return null;
	}

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
			<div className={css(styleSheet.infoBanner)}>
				<InfoIcon />
				<span>Lead parsers will only create contacts from scanned emails.</span>
			</div>
			<div className={css(styleSheet.checkboxContainer)}>
				<Checkbox
					checked={accountsAccount?.features?.automation?.autoStartForLeads ?? false}
					disabled={accountVM.isBusy}
					id='admin-lead-parsers-checkbox'
					name='admin-lead-parsers-checkbox'
					onChange={onLeadParserCheckboxChanged}
				>
					<span>Automatically approve Automations triggered by lead parsers</span>
				</Checkbox>
			</div>
			<div className={css(styleSheet.items)}>
				<ConditionalAccess permission={AdminPermission.ModifyAccount}>
					<BlueCardWithCta
						ctaText='Add custom lead source'
						onClick={() => onAddNewCustomLeadParserClick()}
						styles={[styleSheet.item]}
						text='Are you getting leads from another source?'
					/>
				</ConditionalAccess>
				{/* TODO: the `permission && handler` pattern below (and at the modal props) yields
				    `boolean | function` instead of `function | undefined`. Inherited from AdminIntegrations.
				    Replace with `permission ? handler : undefined` when refactoring this tab. */}
				{emailScannersVm.customLeadParsers?.length > 0 &&
					emailScannersVm.customLeadParsers.map(parser => {
						return (
							<LeadParserCard
								enabled={!parser.isDisabled}
								logo={<div className={css(styleSheet.textLogo)}>{parser.name ?? parser.tag}</div>}
								id={parser.id}
								key={parser.id || parser?.name}
								leadsProcessed={parser.stats?.successCount}
								onEnableClick={adminUserSession.hasPermission(AdminPermission.ModifyAccount) && onEnableClick}
								onManageClick={() => {
									if (parser.isDisabled || !adminUserSession.hasPermission(AdminPermission.ModifyAccount)) {
										return;
									}
									onManageLeadParser(parser);
								}}
								onTestClick={() => {
									if (parser.isDisabled || !adminUserSession.hasPermission(AdminPermission.ModifyAccount)) {
										return;
									}
									onTestLeadParser(parser);
								}}
								tags={[parser.tag]}
								type={Api.EmailScannerType.CustomLeadParser}
								styles={[styleSheet.item]}
							/>
						);
					})}
				{emailScannersVm.emailScanners?.length > 0 &&
					emailScannersVm.emailScanners.map(scanner => {
						return (
							<LeadParserCard
								enabled={!scanner.isDisabled}
								logo={<img src={emailScannerImageUrl(scanner.id)} />}
								id={scanner.id}
								key={scanner.id || scanner?.name}
								leadsProcessed={scanner.stats?.successCount}
								onEnableClick={adminUserSession.hasPermission(AdminPermission.ModifyAccount) && onEnableClick}
								tags={[scanner.tag]}
								type={Api.EmailScannerType.EmailScanner}
								styles={[styleSheet.item]}
							/>
						);
					})}
				<IntegrationCard
					canManage={adminUserSession?.hasPermission(AdminPermission.ModifyAccount)}
					enabled={isMetaAdsEnabled}
					icon={
						<div className={css(styleSheet.metaAdsIcon)}>
							<MetaMainLogo />
						</div>
					}
					key='meta-ads-integration'
					onRenderCtaButton={() => {
						if (!isMetaAdsEnabled) {
							return null;
						}
						return (
							<Button variant='secondary' size='sm' onClick={() => setIsMetaAdsModalOpen(true)}>
								Manage
							</Button>
						);
					}}
					styles={[styleSheet.item]}
				/>
			</div>
			<CustomLeadParserModal
				isProcessing={processing}
				modalProps={{
					isOpen: manageParserModalIsVisible,
					onRequestClose: () => onManageLeadParser(),
				}}
				onCancel={onManageLeadParser}
				onDelete={adminUserSession.hasPermission(AdminPermission.ModifyAccount) && onManageParserDelete}
				onSave={adminUserSession.hasPermission(AdminPermission.ModifyAccount) && onManageParserSave}
				parser={parserToManage}
				tagVM={accountVM}
			/>
			<TestLeadParserModal
				modalProps={{
					isOpen: testParserModalIsVisible,
					onRequestClose: () => onTestLeadParser(),
				}}
				onClose={() => onTestLeadParser()}
				parser={parserToTest}
				tagVM={accountVM}
			/>
			{metaImpersonationContext ? (
				<MetaAdsAdminModal
					impersonationContext={metaImpersonationContext}
					isOpen={isMetaAdsModalOpen}
					setIsOpen={setIsMetaAdsModalOpen}
					onRequestClose={() => setIsMetaAdsModalOpen(false)}
				/>
			) : null}
			{processing && (
				<div className={css(baseStyleSheet.absoluteCenter)}>
					<LoadingSpinner type='large' />
				</div>
			)}
		</div>
	);
};

export const AdminLeadParsers = observer(_AdminLeadParsers);
